import { Button } from '@goodboydigital/astro';
import { Container, Sprite } from 'pixi.js';
import { Signal } from 'typed-signals';
import { Shared } from '../App';

import { PaintIcon } from './PaintIcon';
import { RadioButton, RadioButtonOpts } from './RadioButton';
import { ShopIconSizes } from './ShopIcon';

export enum RadioSelectorStyles
    {
    inline = 0,
    inlineWrap = 1,
    vertical = 2,
}

const defaultOpts = {
    buttons: [] as RadioButtonOpts[] | any[],
    buttonType: RadioButton || Button as any,
    padding: 2,
    multiSelect: false,
    style: RadioSelectorStyles.inline,
    inlineWrapLimit: 100,
};

export type RadioSelectorOpts = typeof defaultOpts;

/**
 * radioSelector's view will always have anchor at (0.5, 0.5)
 */
export class RadioSelector
{
    public view: Sprite;
    public width: number;
    public height: number;

    public signals: {
        valueChanged: Signal<(activeButton: RadioButton, activeIndex: number) => void>
    };

    protected buttonContainer: Container;

    public _buttons: RadioButton[];
    private _padding: number;
    private _inlineWrapLimit: number;
    private _style: RadioSelectorStyles;
    private _activeIndex: number;
    // private _multiSelect: boolean;

    constructor(opts: Partial<RadioSelectorOpts>)
    {
        const o = { ...defaultOpts, ...opts };

        this._padding = o.padding;

        this.view = new Sprite();
        this.view.anchor.set(0.5);

        this.signals = {
            valueChanged: new Signal<(activeButton: RadioButton, activeIndex: number) => void>()
        };

        this.width = 0;
        this.height = 0;

        this._inlineWrapLimit = o.inlineWrapLimit;

        this.buttonContainer = new Container();
        this.buttonContainer.sortableChildren = true;

        this.view.addChild(this.buttonContainer);

        // this._multiSelect = o.multiSelect;

        this._activeIndex = 0;

        this._buttons = [];
        o.buttons.forEach((config, index) =>
        {
            const btn = new o.buttonType(config);

            this._buttons.push(btn);
            if (config.active)
            {
                this._activeIndex = index;
                this.setActive(index);
            }

            this.buttonContainer.addChild(btn.view);
            
            btn.view.on('mousedown', (e: any) => {
                console.log(e)
            });
            
            btn.onPress.connect(() => this.setActive(index));
        });

        this._style = o.style;
        this.repositionButtons();
    }

    public getActiveIndex(): number
    {
        return this._activeIndex;
    }

    public setActive(index: number, silent = false): void
    {
        this._activeIndex = index;

        for (let i = 0; i < this._buttons.length; i++)
        {
            this._buttons[i].state = i === index;
            this._buttons[i].view.zIndex = i === index ? 1 : 0;
            if (!silent) this.signals.valueChanged.emit(this._buttons[index], index);
        }
    }

    public enableAll(): void
    {
        for (let i = 0; i < this._buttons.length; i++)
        {
            this._buttons[i].state = false;
            this._buttons[i].view.zIndex = 0;
        }
    }

    public setSelected(index: number): void
    {
        this._activeIndex = index;

        for (let i = 0; i < this._buttons.length; i++)
        {
            (this._buttons[i] as any as PaintIcon).selected = i === index;
        }
    }

    public repositionButtons(): void
    {
        let left = 0;
        let top = 0;

        switch (this._style)
        {
            case RadioSelectorStyles.inline: {
                this._buttons.forEach((btn) =>
                {
                    btn.view.x = left;
                    left += this._padding + btn.width;

                    if (top < btn.height) top = btn.height;
                });

                this.width = left - this._padding;
                this.height = top;

                this.buttonContainer.x -= (left - this._padding) / 2;
                break;
            }
            case RadioSelectorStyles.inlineWrap: {
                let longestRow = 0;

                this._buttons.forEach((btn) =>
                {
                    if (btn.width > this._inlineWrapLimit)
                    {
                        // if the button itself exceeds the wrap limit, default to displaying them vertically
                        btn.view.x = left;
                        btn.view.y = top;

                        left = 0;
                        top += btn.height + this._padding;

                        if (btn.width > longestRow) longestRow = left;

                        return;
                    }

                    // check if the button fits within the wrap limit, if not, move onto next row
                    if (left + btn.width + this._padding > this._inlineWrapLimit)
                    {
                        left = 0;
                        top += btn.height + this._padding;
                    }

                    btn.view.x = left;
                    btn.view.y = top;

                    // add padding and element width for next button position
                    left += this._padding + btn.width;

                    if (left > longestRow) longestRow = left;
                });

                // subtract padding to leave us with true height/ width for the element
                this.width = longestRow - this._padding;
                this.height = top - this._padding;

                this.buttonContainer.x -= this.width / 2;
                this.buttonContainer.y -= this.height / 2;
                break;
            }
            case RadioSelectorStyles.vertical: {
                this._buttons.forEach((btn) =>
                {
                    btn.view.y = top;
                    top += this._padding + btn.height;

                    if (left < btn.width) left = btn.width;
                });

                this.width = left;
                this.height = top - this._padding;

                this.buttonContainer.y -= (top - this._padding) / 2;
                break;
            }
        }
    }

    public updateWrapLimit(val: number): void
    {
        this._inlineWrapLimit = val;
        if (this._style !== RadioSelectorStyles.inlineWrap) return;

        this.repositionButtons();
    }

    public get style(): RadioSelectorStyles { return this._style; }
    public set style(val: RadioSelectorStyles)
    {
        this._style = val;
        this.repositionButtons();
    }

    public resizeButtons(val: ShopIconSizes): void
    {
        this._buttons.forEach((button) =>
        {
            if ((button as any).setSize) (button as any).setSize(val);
        });
        this.repositionButtons();
    }

}
